import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import {MetaTitle} from '../../../../decorator/meta-title';
import {Report} from '../../../../models/statistic/report';
import {StatisticService} from '../../../../services/statistic/statistic.service';
import {ActivatedRoute} from '@angular/router';
import {LoaderService} from '../../../../services/loader/loader.service';
import {PercentPipe} from '../../../../pipes/percent/percent.pipe';
import {MoneyPipe} from '../../../../pipes/money/money.pipe';
import {CsvExportService} from '../../../../services/csv-export/csv-export.service';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-basic-report',
  imports: [
    NgClass,
    DatePipe,
    PercentPipe,
    MoneyPipe
],
  providers: [DatePipe],
  templateUrl: './basic-report.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './basic-report.component.scss'
})
@MetaTitle('Report')
export class BasicReportComponent implements OnInit, OnDestroy {

  reportType: string = 'daily';
  statistics: Report[] = [];
  sortField: string | null = null;
  sortOrder: 'asc' | 'desc' = 'desc';

  private filtersSubscription: Subscription | null = null;
  private fetchSubscription: Subscription | null = null;
  private exportSubscription: Subscription | null = null;

  constructor(
    protected loader: LoaderService,
    private route: ActivatedRoute,
    private statisticService: StatisticService,
    private csvExport: CsvExportService,
    private datePipe: DatePipe
  ) {
  }

  ngOnInit(): void {
    this.exportSubscription = this.statisticService.$exportCsv.subscribe(() => this.downloadCsv());

    this.route.paramMap.subscribe((params) => {
      this.reportType = params.get('report') ?? 'daily';
      if (!StatisticService.REPORTS.includes(this.reportType)) {
        this.reportType = 'daily';
      }

      this.statistics = [];

      this.filtersSubscription?.unsubscribe();
      this.filtersSubscription = this.statisticService.$filters.subscribe((filters: any) => {
        this.loader.startLoading();

        this.fetchSubscription?.unsubscribe();
        this.fetchSubscription = this.statisticService.fetchBasicReport(this.reportType, filters)
          .subscribe((statistics: Report[]) => {
            this.statistics = statistics;
            this.applySort();
            this.loader.stopLoading();
          });
      });
    });
  }

  ngOnDestroy(): void {
    this.filtersSubscription?.unsubscribe();
    this.fetchSubscription?.unsubscribe();
    this.exportSubscription?.unsubscribe();
    this.loader.stopLoading();
  }

  isDaily(): boolean {
    return this.reportType === 'daily';
  }

  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortOrder = 'desc';
    }

    this.applySort();
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) {
      return 'bi-arrow-down-up';
    }

    return this.sortOrder === 'asc' ? 'bi-sort-up' : 'bi-sort-down';
  }

  downloadCsv(): void {
    if (this.loader.isLoading() || !this.statistics.length) {
      return;
    }

    const headers = [
      this.isDaily() ? 'Date' : 'Label',
      'Clicks',
      'Conversions',
      'Conversion Rate (%)',
      'Revenue',
      'EPC',
      'CPL',
    ];

    const rows = this.statistics.map((statistic: Report, index: number) => [
      index === this.statistics.length - 1
        ? 'Totals'
        : (this.isDaily() ? this.datePipe.transform(statistic.getDate(), 'dd/MM/yyyy') : statistic.toString()),
      statistic.clicks ?? 0,
      statistic.conversions ?? 0,
      this.csvExport.formatPercent(statistic.conversionRate ?? 0),
      this.csvExport.formatMoney(statistic.revenue ?? 0),
      this.csvExport.formatMoney(statistic.epc ?? 0),
      this.csvExport.formatMoney(statistic.cpl ?? 0),
    ]);

    this.csvExport.download(this.reportType + '-report.csv', headers, rows);
  }

  private applySort(): void {
    // Last row is the totals row, it stays at the bottom
    if (!this.sortField || this.statistics.length < 3) {
      return;
    }

    const rows = this.statistics.slice(0, -1);
    const totals = this.statistics[this.statistics.length - 1];

    rows.sort((a: any, b: any) => {
      const aValue = a[this.sortField as string] ?? 0;
      const bValue = b[this.sortField as string] ?? 0;

      return this.sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

    this.statistics = [...rows, totals];
  }

}
